/* eslint-disable react/prop-types */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  ButtonPrimary,
  ButtonTertiary,
  Checkbox,
  Heading,
  IconButton,
  IconClose,
  Input,
} from '@americanexpress/dls-react';
import OverflowItem from '../../OverflowItem/OverflowItem';
import { saveSearchListStyle } from '../../../styles/AdvancedFormSearch/AdvStyleList';

const AdvUpdateSearch = ({
  pageData,
  savedSearchList,
  onItemClick,
  handleFormReset,
  showPopup,
  setShowPopup,
  selectedItemName,
  selectedItemId,
  isIncludeDateTimeChecked,
}) => {
  const [searchName, setSearchName] = useState(selectedItemName || '');
  const [includeDateTime, setIncludeDateTime] = useState(isIncludeDateTimeChecked);
  const showDateTimeCheckbox = pageData?.saveSearchProp?.showSaveDateTimeCheckbox || false;
  const [errorMsg, setErrorMsg] = useState('');
  const [status, setStatus] = useState('default');
  const ModalRef = useRef(null);

  // Keep searchName in sync with selectedItemName, but only set if searchName is empty
  useEffect(() => {
    if (!searchName && selectedItemName) {
      setSearchName(selectedItemName);
    }
  }, [selectedItemName]);

  const onClose = () => {
    setStatus('default');
    setErrorMsg('');
    setShowPopup(!showPopup);
  };

  const handleClickOutside = (event) => {
    if (ModalRef.current && !ModalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    if (!showPopup) return;
    document.addEventListener('mousedown', handleClickOutside, true); // use capture phase
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [onClose]);

  const onItemActionClickHandler = (action, item) => {
    onItemClick(action, item);
    onClose();
  };

  const onUpdateButtonClick = () => {
    if (searchName === '') {
      setStatus('error');
      setErrorMsg('Please provide the name');
      return;
    }

    if (savedSearchList.filter((item) => {
      if (item.id === selectedItemId) return false;
      return (item.searchName || '').trim().toUpperCase() === (searchName || '').trim().toUpperCase();
      }).length > 0) {
      setStatus('error');
      setErrorMsg('This name already exists');
      return;
    }

    const currentSearch = {};
    pageData.formFields.forEach((field) => {
      currentSearch[field.name] = field.value;
    });
    if (showDateTimeCheckbox && !includeDateTime) currentSearch.startEndDateTime = '' ;

    let setError = false;
    let similarSearch = '';
    savedSearchList.forEach((obj) => {
      let count = 0;
      pageData.formFields.forEach((key) => {
        if (currentSearch[key.name]?.toString().toUpperCase()
          === obj?.metaDataJson[key.name]?.toString().toUpperCase()) {
          // eslint-disable-next-line no-plusplus
          count++;
        }
      });
      if (count === pageData.formFields.length) {
        setError = true;
        similarSearch = obj.searchName;
      }
    });
    if (setError) {
      setStatus('error');
      setErrorMsg(`'${similarSearch}' is similar to the current search, please delete the old search to save the new one!`);
      return;
    }

    currentSearch.searchName = searchName;
    currentSearch.id = selectedItemId;
    onItemActionClickHandler('UPDATE', currentSearch);
    handleFormReset();
  };

  return (
    <div id="saveSearch-action-component" style={{ display: 'inline-block'}} ref={ModalRef}>
      <OverflowItem
        labelComponent={(
          <ButtonTertiary
            type="button"
            id="AdvFormActionButtons-updateSaveSearch-form"
            className="col-md-1 margin-responsive-r"
            onClick={onClose}
            css={{ marginLeft: '20px' }}
          >
            Update Current Saved Search
          </ButtonTertiary>
        )}
        width="350"
        id="item-action-overflowItem"
        isLabelComponentClicked={showPopup}
        isOutsideClick={false}
      >
        <div
          aria-label="Popup Action List"
          id="item-action-navigation"
          css={saveSearchListStyle['.navigation']}
        >
          <div css={saveSearchListStyle['.header']}>
            <Heading
              id="AdvFormUpdateSaveSearch-formHeader"
              level={1}
              className="dls-gray-06"
              headingStyle="heading3"
            >
              Update Current Saved Search As
            </Heading>
            <IconButton
              style={{ width: '5px', marginLeft: '15px' }}
              id="AdvFormSaveSearch-advUpdateSearchClose"
              styleType="tertiary"
              icon={<IconClose size="sm" />}
              onClick={onClose}
            />
          </div>
          <div>
            <div css={saveSearchListStyle['.navigationItemList']}>
              <Input
                value={searchName}
                id="update-input-field"
                onChange={(e) => {
                  const trimmed = e.target.value.trim();
                  setSearchName(trimmed.slice(0, 24));
                }}
              />
              {showDateTimeCheckbox && (
                <Checkbox
                  id="update-datetime-checkbox"
                  value={includeDateTime}
                  checked={includeDateTime}
                  label="Include Start & End Date"
                  defaultChecked={false}
                  onChange={() => setIncludeDateTime(!includeDateTime)}
                />
              )}
              {status === 'error' && (
                <Alert
                  css={saveSearchListStyle['.saveSection']}
                  style={{ margin: 'auto' }}
                  id="success-primary-error-alert"
                  status={status}
                  showStateIcon={true}
                  show={true}
                >
                  {errorMsg}
                </Alert>
              )}
              <ButtonPrimary
                id="AdvFormActionButtons-updateSearch-form"
                type="submit"
                aria-label="Update"
                onClick={() => onUpdateButtonClick(searchName)}
                theme={saveSearchListStyle['.saveButtonTheme']}
                style={saveSearchListStyle['.saveButton']}
              >
                Update
              </ButtonPrimary>
            </div>
          </div>
        </div>
      </OverflowItem>
    </div>
  );
};

AdvUpdateSearch.displayName = 'AdvUpdateSearch';

AdvUpdateSearch.propTypes = {
  pageData: PropTypes.shape({
    formHeader: PropTypes.string,
    formFields: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
      })
    ),
  }),
  setPageData: PropTypes.func.isRequired,
  savedSearchList: PropTypes.arrayOf(PropTypes.shape({})),
  onItemClick: PropTypes.func.isRequired,
  showPopup: PropTypes.bool.isRequired,
  setShowPopup: PropTypes.func.isRequired,
};

export default AdvUpdateSearch;
